import i18nFactory from '@yandex-int/i18n';
import { Tumbler } from 'lego-on-react';
import * as React from 'react';

import { Bem } from '../../service/Bem/Bem';
import {
    calcSectionsTree,
    calcSegmentsRatesTree,
    calcSegmentsTree,
    changeRate,
    Rate,
    Section,
    Segment,
    toggleSection,
    toggleSegment,
    toggleTargetingStatus,
    TreeSection
} from '../../service/Experiments/Experiments';
import { pluralizeWord } from '../../service/Pluralize/Pluralize';
import { ExperimentsSelector } from '../ExperimentsSelector/ExperimentsSelector';
import { ExSegmentsRates } from '../ExSegmentsRates/ExSegmentsRates';
import { ExSegmentsSelector } from '../ExSegmentsSelector/ExSegmentsSelector';

import './ExperimentsControl.css';
import * as keyset from './ExperimentsControl.i18n';

const i18n = i18nFactory(keyset);

interface ExperimentsControlProps {
    onChange: (state: Partial<ExperimentsControlProps>) => void;
    segmentInverseIndex: Record<string, Section['section_id']>;
    treeData: Record<string, Section>;
    treeOrder: Array<TreeSection>;
    isTargetingEmpty: boolean;
    isTargetingActive: boolean;
    isRatesActive: boolean;
    isShowOnlySelected: boolean;
    view?: 'classic' | 'default';
}

export class ExperimentsControl extends React.Component<
    ExperimentsControlProps
> {
    public static defaultProps: Partial<ExperimentsControlProps> = {
        view: 'default'
    };

    public getExperimentsArchiveListMessage() {
        const { treeData } = this.props;

        return Object.keys(treeData).reduce(function(
            archiveList: string[],
            experiment: string
        ): string[] {
            if (
                treeData[experiment].selected &&
                treeData[experiment].archive === 1
            ) {
                archiveList.push(treeData[experiment].section_name);
            }

            return archiveList;
        },
        []);
    }

    render() {
        const {
            view,
            treeOrder,
            treeData,
            isTargetingActive,
            isRatesActive,
            isShowOnlySelected
        } = this.props;

        const experimentsArchiveList = this.getExperimentsArchiveListMessage();
        const experimentsArchiveListMessage = experimentsArchiveList.join(', ');

        return (
            <div className={Bem.cls('experiments-control')}>
                <p className={Bem.cls('experiments-control', 'title')}>
                    {i18n('title')}
                </p>
                <p className={Bem.cls('experiments-control', 'subtitle')}>
                    {i18n('subtitle')}
                </p>
                <div className={Bem.cls('experiments-control', 'settings')}>
                    <ExperimentsSelector
                        treeOrder={calcSectionsTree(
                            treeData,
                            treeOrder,
                            isShowOnlySelected
                        )}
                        treeData={treeData}
                        isShowOnlySelected={isShowOnlySelected}
                        onChange={this.onSectionSelectChange}
                        view={view}
                    />
                    <ExSegmentsSelector
                        cls={Bem.cls('experiments-control', 'segments')}
                        treeOrder={calcSegmentsTree(
                            treeData,
                            treeOrder,
                            isShowOnlySelected
                        )}
                        treeData={treeData}
                        isShowOnlySelected={isShowOnlySelected}
                        isActive={isTargetingActive}
                        view={view}
                        onChange={this.onSegmentSelectChange}
                        onActiveChange={this.onTargetingActiveChange}
                    />
                    <ExSegmentsRates
                        cls={Bem.cls('experiments-control', 'rates')}
                        treeOrder={calcSegmentsRatesTree(
                            treeData,
                            treeOrder,
                            isShowOnlySelected,
                            isTargetingActive
                        )}
                        treeData={treeData}
                        isShowOnlySelected={isShowOnlySelected}
                        isActive={isRatesActive}
                        view={view}
                        onChange={this.onRateChange}
                        onActiveChange={this.onRatesActiveChange}
                    />
                </div>
                <div className={Bem.cls('experiments-control', 'controls')}>
                    <div
                        className={Bem.cls(
                            'experiments-control',
                            'only-selected'
                        )}
                    >
                        <Tumbler
                            cls={Bem.cls(
                                'experiments-control',
                                'only-selected-switcher'
                            )}
                            theme="normal"
                            view={'default'}
                            size={'xs'}
                            checked={isShowOnlySelected}
                            onChange={this.onShowSelectedChange}
                        >
                            <Tumbler.Off
                                id="random-id-for-ts2"
                                text={i18n('show-only-selected')}
                                side="right"
                            />
                        </Tumbler>

                        <p
                            className={Bem.cls(
                                'experiments-control',
                                'warning'
                            )}
                        >
                            {pluralizeWord(
                                [
                                    i18n('experiments-archive-message-single', {
                                        experimentsList: experimentsArchiveListMessage
                                    }),
                                    i18n(
                                        'experiments-archive-message-multiply',
                                        {
                                            experimentsList: experimentsArchiveListMessage
                                        }
                                    )
                                ],
                                experimentsArchiveList.length
                            )}
                        </p>
                    </div>
                </div>
            </div>
        );
    }

    /**
     * Обработчик изменения выбранных экспериментов
     */
    private onSectionSelectChange = (data: {
        checked: boolean;
        item: Section;
    }): void => {
        const { treeData, onChange } = this.props;

        onChange({
            treeData: toggleSection(treeData, data.item.section_id)
        });
    };

    /**
     * Обработчик изменения выбранных сегментов
     * @param {Object} data
     */
    private onSegmentSelectChange = (data: {
        checked: boolean;
        item: Segment;
    }): void => {
        const { treeData, segmentInverseIndex, onChange } = this.props;

        onChange({
            treeData: toggleSegment(
                treeData,
                data.item.segment_id,
                segmentInverseIndex[data.item.segment_id]
            )
        });
    };

    /**
     * Обработчик изменения тумблера "Показать только выбранные"
     */
    private onShowSelectedChange = (): void => {
        const { isShowOnlySelected, onChange } = this.props;

        onChange({
            isShowOnlySelected: !isShowOnlySelected
        });
    };

    /**
     * Обработчик тумблера включение нацеливания
     */
    private onTargetingActiveChange = () => {
        const {
            isTargetingActive,
            isTargetingEmpty,
            treeData,
            onChange
        } = this.props;

        onChange({
            treeData: toggleTargetingStatus(
                treeData,
                isTargetingEmpty,
                !isTargetingActive
            ),
            isTargetingEmpty: false,
            isTargetingActive: !isTargetingActive
        });
    };

    /**
     * Обработчик тумблера включение нацеливания
     */
    private onRatesActiveChange = () => {
        const { isRatesActive, onChange } = this.props;

        onChange({
            isRatesActive: !isRatesActive
        });
    };

    private onRateChange = (segmentId: Segment['segment_id'], data: Rate) => {
        const { treeData, segmentInverseIndex, onChange } = this.props;
        const sectionId = segmentInverseIndex[segmentId];

        onChange({
            treeData: changeRate(treeData, sectionId, segmentId, data)
        });
    };
}
